import type { InfiniteData, Query, QueryClient } from '@tanstack/react-query';
import { hashKey } from '@tanstack/react-query';

import { getQueryClient } from '../queryClientSingleton';
import type {
    CreatePaginationEntityOptions,
    QueryPageReturnType,
    QueryParamsBaseType,
} from './createPaginationEntityTypes';
import { KeyNamespaces, getKey } from '../utils/getKey';
import type { ApiSliceKey } from '../utils/sliceSelectorsTypes';
import type { CacheKey } from '../utils/internalTypings';
import { setQueryDataWithDefaults } from '../utils/setQueryDataWithDefaults';

interface Builder<T> {
    all: ApiSliceKey;
    lists: () => ApiSliceKey;
    list: (params?: T) => ApiSliceKey;
    details: () => ApiSliceKey;
    detail: (id: string) => ApiSliceKey;
}

/**
 * Creates helper api for referencing cache keys
 */
export function createCacheKeyBuilder<T>(base: string): Builder<T> {
    const builder: Builder<T> = {
        all: getKey(KeyNamespaces.PAGINATION_ENTITY, { name: base }),
        lists: () => [...builder.all, 'list'],
        list: (params?: T) => [...builder.lists(), hashKey([{ params: params ?? {} }])],
        details: () => [...builder.all, 'detail'],
        detail: (id: string) => [...builder.details(), `${id}`],
    };
    return builder;
}

type CacheUpdatesRecord<Entity> = Map<string, Entity>;

/**
 * Creates helper object used when making updates
 */
function createCacheUpdatesRecord<Entity>(
    entities: Entity[],
    selectId: CacheKey<Entity>,
): CacheUpdatesRecord<Entity> {
    return entities.reduce((result, entity) => {
        result.set(selectId(entity), entity);

        return result;
    }, new Map());
}

type UpdateCacheOperation = 'APPEND' | 'REMOVE' | 'NONE';

function updatePageCache<Entity>(
    currentEntitiesData: InfiniteData<QueryPageReturnType<Entity>> | undefined,
    updates: CacheUpdatesRecord<Entity>,
    {
        operation,
        selectId,
    }: {
        selectId: CacheKey<Entity>;
        operation?: UpdateCacheOperation;
    },
) {
    if (!currentEntitiesData) {
        return currentEntitiesData;
    }

    // we use react-query's infinite query, which data structure is in the form of
    // {pages: [], pageParams:[]}
    return {
        ...currentEntitiesData,
        pages: currentEntitiesData.pages?.map((page) => {
            const pageMap = createCacheUpdatesRecord(page.data, selectId);

            updates.forEach((updateValue, updateId) => {
                // force append if item not present
                if (!pageMap.has(updateId) && operation === 'APPEND') {
                    pageMap.set(updateId, updateValue);
                }
                // update existing item
                if (pageMap.has(updateId)) {
                    pageMap.set(updateId, updateValue);
                }
                // remove existing item
                if (pageMap.has(updateId) && operation === 'REMOVE') {
                    pageMap.delete(updateId);
                }
            });
            const newData = Array.from(pageMap.values());
            const countChange = newData.length - page.data.length;

            return {
                ...page,
                data: newData,
                totalElements: page.totalElements && page.totalElements + countChange,
                pageCount: page.pageCount + countChange,
            };
        }),
    };
}

/**
 * Syncs data for all keys in the state
 */
export function updateEntityListCache<Entity, QueryParams extends QueryParamsBaseType>(
    entities: Entity[],
    {
        exclude,
        params,
        name,
        selectId,
        operation,
    }: Pick<CreatePaginationEntityOptions<Entity, QueryParams>, 'name'> & {
        exclude?: ApiSliceKey;
        params?: QueryParams;
        selectId: CacheKey<Entity>;
        operation?: UpdateCacheOperation;
    },
    client = getQueryClient(),
): void {
    const cache = client.getQueryCache();
    const updates = createCacheUpdatesRecord(entities, selectId);
    const queryKey = params && createCacheKeyBuilder(name).list(params);

    cache.findAll({ queryKey: createCacheKeyBuilder(name).lists() }).forEach((query) => {
        if (exclude && query.queryHash === hashKey(exclude)) {
            return;
        }
        if (queryKey && query.queryHash !== hashKey(queryKey)) {
            return;
        }

        if (query.state.isInvalidated) {
            return;
        }

        setQueryDataWithDefaults(
            client,
            query.queryKey,
            (currentEntitiesData?: InfiniteData<QueryPageReturnType<Entity>>) => {
                return updatePageCache(currentEntitiesData, updates, {
                    operation,
                    selectId,
                });
            },
        );
    });
}

export function removeFromEntityListCache<Entity, QueryParams extends QueryParamsBaseType>(
    entities: string[],
    {
        exclude,
        name,
        selectId,
    }: Pick<CreatePaginationEntityOptions<Entity, QueryParams>, 'name'> & {
        exclude?: ApiSliceKey;
        selectId: CacheKey<Entity>;
    },
    client = getQueryClient(),
): void {
    const cache = client.getQueryCache();
    const updates = entities.reduce((acc, curr) => {
        acc.set(curr, {} as Entity);
        return acc;
    }, new Map<string, Entity>());

    cache.findAll({ queryKey: createCacheKeyBuilder(name).lists() }).forEach((query) => {
        if (exclude && query.queryHash === hashKey(exclude)) {
            return;
        }

        if (query.state.isInvalidated) {
            return;
        }

        setQueryDataWithDefaults(
            client,
            query.queryKey,
            (currentEntitiesData?: InfiniteData<QueryPageReturnType<Entity>>) => {
                return updatePageCache(currentEntitiesData, updates, {
                    operation: 'REMOVE',
                    selectId,
                });
            },
        );
    });
}

/**
 * Syncs data for single entity detail in the state
 */
export function updateEntityCache<Entity>(
    response: Entity,
    id: string,
    options: Pick<CreatePaginationEntityOptions<Entity>, 'name'>,
    client = getQueryClient(),
): void {
    const queryKey = createCacheKeyBuilder(options.name).detail(id);

    if (client.getQueryState(queryKey)?.isInvalidated) {
        return;
    }

    setQueryDataWithDefaults(client, queryKey, () => response);
}

/**
 * Syncs entity data with the state
 */
export function updateCache<Entity>(
    {
        entity,
        name,
        selectId,
    }: {
        entity: Entity;
        name: string;
        selectId: CacheKey<Entity>;
    },
    client = getQueryClient(),
): void {
    updateEntityCache(
        entity,
        selectId(entity),
        {
            name,
        },
        client,
    );

    updateEntityListCache(
        [entity],
        {
            name,
            selectId,
        },
        client,
    );
}

/**
 * Clears and invalidates all list queries
 */
export function replaceListEntities<QueryParams extends QueryParamsBaseType, Entity>(
    {
        params,
        name,
        entities,
        selectId,
    }: { entities: Entity[]; params?: QueryParams; name: string; selectId: CacheKey<Entity> },
    client = getQueryClient(),
): void {
    const queryKey = params && createCacheKeyBuilder(name).list(params);

    client
        .getQueryCache()
        .findAll({ queryKey: createCacheKeyBuilder(name).lists() })
        .forEach((query) => {
            if (queryKey && query.queryHash !== hashKey(queryKey)) {
                return;
            }

            setQueryDataWithDefaults(client, query.queryKey, () => {
                return {
                    pageParams: [],
                    pages: [
                        {
                            data: entities,
                            totalElements: entities.length,
                            pageCount: 0,
                        },
                    ],
                };
            });
        });

    entities.forEach((entity) => {
        updateEntityCache(
            entity,
            selectId(entity),
            {
                name,
            },
            client,
        );
    });
}

/**
 * Removes the query if it has no observers; otherwise, invalidates the query.
 *
 * If the query has observers, invalidate it to ensure they refetch and receive fresh data.
 * If there are no observers (no component uses the query via hook), remove the query to prevent storing
 * stale data in the cache
 */
function removeOrInvalidateQuery(query: Query, client: QueryClient, refetchActive: boolean): void {
    if (query.getObserversCount() === 0) {
        client.removeQueries({
            queryKey: query.queryKey,
            exact: true,
        });
        return;
    }

    if (refetchActive) {
        setQueryDataWithDefaults(client, query.queryKey, () => null);
    }

    client.invalidateQueries({
        queryKey: query.queryKey,
        refetchType: refetchActive ? 'active' : 'none',
        exact: true,
    });
}

/**
 * Clears and invalidates all list queries
 */
export function invalidateListEntities<QueryParams extends QueryParamsBaseType>(
    {
        params,
        name,
        refetchActive = true,
    }: { params?: QueryParams; name: string; refetchActive?: boolean },
    client = getQueryClient(),
): void {
    const queryKey = params && createCacheKeyBuilder(name).list(params);

    client
        .getQueryCache()
        .findAll({ queryKey: createCacheKeyBuilder(name).lists() })
        .forEach((query) => {
            if (queryKey && query.queryHash !== hashKey(queryKey)) {
                return;
            }
            removeOrInvalidateQuery(query, client, refetchActive);
        });
}

/**
 * Clears and invalidates all queries by id
 */
export function invalidateEntity(
    { name, id, refetchActive = true }: { name: string; id: string; refetchActive?: boolean },
    client = getQueryClient(),
): void {
    const detailQueryKey = createCacheKeyBuilder(name).detail(id);
    const query = client.getQueryCache().find({ queryKey: detailQueryKey, exact: true });

    if (query) {
        removeOrInvalidateQuery(query, client, refetchActive);
    }
}

/**
 * Clears and invalidates all cached entities
 */
export function invalidateAll(
    { name, refetchActive = true }: { name: string; refetchActive: boolean },
    client = getQueryClient(),
): void {
    client
        .getQueryCache()
        .findAll({ queryKey: createCacheKeyBuilder(name).all })
        .forEach((query) => removeOrInvalidateQuery(query, client, refetchActive));
}
